import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet';
import "./whyUnique.css";

const DIFFERENT_CARDS = [
  {
    title: "Personal Attention",
    desc: "Small batch sizes ensuring every student gets the focus they need to thrive.",
    icon: "person",
  },
  {
    title: "Safe Space",
    desc: "A judgment-free zone where asking  doubts is encouraged, not mocked.",
    icon: "favorite",
  },
  {
    title: "Mentorship Beyond Marks",
    desc: "Guidance on handling pressure, time management, and emotional well-being.",
    icon: "groups",
  },
  {
    title: "Concept Clarity",
    desc: "Focusing on the “why” and “how” behind every formula, not rote memorization.",
    icon: "lightbulb",
  },
  {
    title: "Mental Wellness",
    desc: "Regular sessions dedicated to stress reduction and maintaining a healthy mindset.",
    icon: "spa",
  },
  {
    title: "Parent and Student Counselling",
    desc: "Transparent communication keeping parents involved as supporters, not stressors.",
    icon: "handshake",
  },
  {
    title: "Custom Pace",
    desc: "Adaptive learning plans that respect individual learning speeds and styles.",
    icon: "speed",
  },
  {
    title: "Future Ready",
    desc: "Building critical thinking skills that serve students well beyond their exams.",
    icon: "rocket_launch",
  },
];

const ELITE_CARDS = [
  {
    title: "AI BASED S-CUBUS Student App",
    points: [
      "Real-time academic analytics",
      "Personalized weakness detection",
      "Rank projection insights",
      "Daily progress tracking",
      "Smart improvement strategies",
    ],
    icon: "smartphone",
  },
  {
    title: "S-CUBUS Parent App",
    points: [
      "Live attendance monitoring",
      "Performance comparison reports",
      "Instant notifications",
      "Complete transparency",
      "Excellence requires clarity",
    ],
    icon: "groups",
  },
  {
    title: "S-CUBUS Study Material",
    points: [
      "Strengthen conceptual clarity",
      "Develop analytical reasoning",
      "Enhance problem-solving speed",
      "Train multi-step logical thinking",
      "Think Deeper. Perform Better.",
    ],
    icon: "menu_book",
  },
];

const WhyUnique: React.FC = () => {
  const fullMapUrl =
    "https://www.google.com/maps/place/Wakefit+-+Furniture+Store+In+Dwarka,+Delhi/@28.5945614,77.039648,17z/data=!4m6!3m5!1s0x390d1b228be52fa1:0x7cff3d4cacf387d5!8m2!3d28.5945567!4d77.0422283!16s%2Fg%2F11txy1f7gs?entry=ttu&g_ep=EgoyMDI2MDIwNC4wIKXMDSoKLDEwMDc5MjA3M0gBUAM%3D";
  const mentorPhoneDial = "+9101146696001";

  return (
    <div className="wu-page">
      <Helmet>
        <title>Why S-CUBUS Is Different | JEE, NEET & Olympiad Coaching</title>
        <meta 
          name="description" 
          content="Discover why S-CUBUS stands apart with expert faculty, personal mentorship, concept-focused learning, disciplined preparation and result-oriented coaching for JEE, NEET & Olympiads." 
        />
        <meta 
          name="keywords" 
          content="why choose S-CUBUS, S-CUBUS coaching institute, S-CUBUS unique features, S-CUBUS advantage, best coaching institute in Dwarka, JEE coaching in Dwarka, NEET coaching in Dwarka, Olympiad coaching in Dwarka, Foundation coaching in Dwarka, JEE NEET Olympiad coaching, personalized coaching for JEE NEET, concept based learning, result oriented coaching, personal mentorship for students, expert faculty for JEE NEET, disciplined learning environment" 
        />
        <link rel="canonical" href="https://www.scubus.com/why-unique" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Why S-CUBUS Is Different | JEE, NEET & Olympiad Coaching" />
        <meta property="og:description" content="Discover why S-CUBUS stands apart with expert faculty, personal mentorship, concept-focused learning, disciplined preparation and result-oriented coaching for JEE, NEET & Olympiads." />
        <meta property="og:url" content="https://www.scubus.com/why-unique" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:title" content="Why S-CUBUS Is Different | JEE, NEET & Olympiad Coaching" />
        <meta name="twitter:description" content="Discover why S-CUBUS stands apart with expert faculty, personal mentorship, concept-focused learning, disciplined preparation and result-oriented coaching for JEE, NEET & Olympiads." />
      </Helmet>

      {/* HERO QUOTE */}
      <section className="wu-hero">
        <div className="wu-hero__content">
          <div className="wu-hero__text">
            <h1 className="wu-hero__quote">
              Why Choose S-CUBUS? What Makes Us Different
            </h1>
            <p className="wu-hero__subtext">
              Because every Student deserves to be understood, guided, and believed in.
            </p>
          </div>
          <div
            className="wu-hero__visual"
            aria-hidden="true"
            style={{ backgroundImage: "url('/mode/cheer2.png')" }}
          />
        </div>
      </section>

      {/* OUR PHILOSOPHY */}
      <section className="wu-section">
        <div className="wu-container wu-philosophy">
          <div className="wu-philosophy__image">
            <img
              src="/logo/logo.png"
              alt="Mentors guiding students"
            />
            <span className="wu-imageTag">MENTORSHIP</span>
          </div>

          <div className="wu-philosophy__content">
            <div className="wu-eyebrow">
              <span className="wu-eyebrow__line" />
              <span>OUR PHILOSOPHY</span>
            </div>

            <h2 className="wu-title">More Than Just Syllabus Coverage</h2>

            <p className="wu-text">
              At S-CUBUS, we believe that effective teaching isn’t merely about completing chapters or solving equations.
              It’s about deeply understanding the unique fears, aspirations, and hidden potential within every learner.
            </p>

            <div className="wu-quoteBox">
              <span className="wu-quoteBox__bar" />
              <p>“We don’t rush students. We walk with them.”</p>
            </div>

            <p className="wu-text">
              The journey to JEE, NEET, or Olympiads is marathon, not a sprint. We build the stamina of the mind and
              the resilience of the spirit, ensuring that no question goes unanswered and no doubt remains unspoken.
            </p>

            <Link className="wu-link" to="/about">
              Read about our Methodology <span className="wu-link__arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* WHAT MAKES US DIFFERENT */}
      <section className="wu-section wu-section--white">
        <div className="wu-container">
          <div className="wu-what-layout">
            <div className="wu-what-image">
              <picture>
                <source media="(max-width: 767px)" srcSet="/mode/card_4.png" />
                <img
                  src="/mode/card_pose.png"
                  alt="Students benefiting from our unique approach"
                  loading="lazy"
                />
              </picture>
            </div>
            <div className="wu-what-content">
              <h2 className="wu-what-title">What Makes Us Different</h2>
              <p className="wu-what-sub">
                We’ve reimagined the coaching experience to put the student at the center of everything we do.
              </p>

              <div className="wu-grid">
                {DIFFERENT_CARDS.map((c) => (
                  <article className="wu-card" key={c.title}>
                    <div className="wu-card__icon">
                      <span className="material-symbols-outlined">{c.icon}</span>
                    </div>
                    <h3 className="wu-card__title">{c.title}</h3>
                    <p className="wu-card__text">{c.desc}</p>
                  </article>
                ))}
              </div>

              <section className="wu-elite" aria-label="The Elite Advantage">
                <h3 className="wu-elite__title">The Elite Advantage</h3>
                <div className="wu-elite__grid">
                  {ELITE_CARDS.map((c) => (
                    <article className="wu-eliteCard" key={c.title}>
                      <div className="wu-eliteCard__icon">
                        <span className="material-symbols-outlined">{c.icon}</span>
                      </div>
                      <h4 className="wu-eliteCard__title">{c.title}</h4>
                      <ul className="wu-eliteCard__list">
                        {c.points.map((point) => (
                          <li className="wu-eliteCard__item" key={point}>
                            {point}
                          </li>
                        ))}
                      </ul>
                    </article>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="wu-bottom">
        <div className="wu-container wu-bottom__inner">
          <h2 className="wu-bottom__title">Experience the S-CUBUS Because Every Student Learns Better With the Right Support.</h2>
          <p className="wu-bottom__text">
            We invite you and your child to visit our center, meet our mentors, and feel the supportive environment we’ve created and discover how learning feels when it finally clicks.
          </p>

          <div className="wu-actions">
            <button
              className="wu-btn wu-btn--primary"
              type="button"
              onClick={() => window.open(fullMapUrl, "_blank")}
            >
              Visit our Center
            </button>
            <button
              className="wu-btn wu-btn--secondary"
              type="button"
              onClick={() => (window.location.href = `tel:${mentorPhoneDial}`)}
            >
              Talk to a Mentor
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyUnique;
